import { Component, EventEmitter, inject, Input, OnDestroy, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription, finalize } from 'rxjs';
import { WirelessSetupService } from '../../../common/services/wireless-setup.service';
import { NotificationService } from '../../../common/services/notification.service';
import { SpinnerService } from '../../../common/services/spinner.service';
import { LanguageStateService } from '../../../common/services/language-state.service';

export interface PinSentEvent {
  selectedMethod: string;
}

@Component({
    selector: 'app-otp-delivery',
    templateUrl: './otp-delivery.component.html',
    standalone: false
})
export class OtpDeliveryComponent implements OnDestroy {
  @Input() wirelessSetupCustomerOrderPayload: any;
  @Input() accountNumber: number;
  @Input() pinDeliveryOptions: { email?: string; ctnOptions: string[] } = { ctnOptions: [] };
  @Input() isAccountValidated = false;
  @Output() pinSent = new EventEmitter<PinSentEvent>();
  @Output() pinExpired = new EventEmitter<void>();
  @Output() connectionError = new EventEmitter<void>();

  fb: FormBuilder = inject(FormBuilder);
  wirelessSetupService = inject(WirelessSetupService);
  notificationService = inject(NotificationService);
  spinnerService = inject(SpinnerService);
  languageService: LanguageStateService = inject(LanguageStateService);

  otpForm: FormGroup = this.fb.group({
    deliveryMethod: ['', [Validators.required]],
  });

  selectedPinDeliveryMethod: string | null = null;
  showSendPinButton = true;
  hasSentPinOnce = false;
  pinSentSuccess = false;
  resendCooldownSeconds = 0;

  step2ErrorType: 'expired' | 'notDelivered' | null = null;

  private subscriptions = new Subscription();
  private resendTimerId: ReturnType<typeof setInterval> | null = null;
  private readonly RESEND_COOLDOWN_SECONDS = 60;
  private pinAlreadyValidated = false;

  selectPinDeliveryMethod(method: string) {
    this.selectedPinDeliveryMethod = method;
    this.otpForm.get('deliveryMethod')?.setValue(method);
  }

  maskEmail(email?: string): string {
    if (!email) return '';
    const [local, domain] = email.split('@');
    if (!domain || local.length <= 3) return email;
    const masked = local.slice(0, 2) + '-'.repeat(local.length - 3) + local.slice(-1);
    return `${masked}@${domain}`;
  }

  sendButtonLabelKey(): string {
    return this.hasSentPinOnce ? 'RESEND_PIN' : 'SEND_PIN';
  }

  sendPinClicked() {
    if (!this.selectedPinDeliveryMethod) {
      this.otpForm.get('deliveryMethod')?.markAsTouched();
      return;
    }

    this.spinnerService.setLoading(true, 'otp-delivery-sendPin');
    this.step2ErrorType = null;

    const method = this.selectedPinDeliveryMethod;
    const isEmailSelected = method === 'email';
    const body: { AccountNumber: string; TransactionNumber?: string; Email?: string } = {
      AccountNumber: String(this.accountNumber)
    };
    if (isEmailSelected) {
      body.Email = this.pinDeliveryOptions.email;
    } else {
      body.TransactionNumber = this.sanitizePhoneNumber(method);
    }

    this.subscriptions.add(
      this.wirelessSetupService.sendPin(body, this.wirelessSetupCustomerOrderPayload)
        .pipe(finalize(() => {
          this.spinnerService.setLoading(false, 'otp-delivery-sendPin');
          this.hasSentPinOnce = true;
        }))
        .subscribe({
          next: (resp: any) => {
            const viResp = this.retrieveWirelessResponse(resp);
            if (viResp.Code === '0000') {
              this.pinSentSuccess = true;
              this.pinAlreadyValidated = false;
              this.startResendCooldown();
              this.pinSent.emit({ selectedMethod: method });
            } else {
              this.pinSentSuccess = false;
              this.clearResendCooldown();
              this.step2ErrorType = 'notDelivered';
            }
          },
          error: () => {
            this.connectionError.emit();
          }
        })
    );
  }

  markPinValidated() {
    this.pinAlreadyValidated = true;
    this.clearResendCooldown();
  }

  private startResendCooldown() {
    this.clearResendCooldown();
    this.resendCooldownSeconds = this.RESEND_COOLDOWN_SECONDS;
    this.resendTimerId = setInterval(() => {
      const remaining = this.resendCooldownSeconds - 1;
      if (remaining <= 0) {
        this.resendCooldownSeconds = 0;
        this.clearResendCooldown();
        if (!this.pinAlreadyValidated) {
          this.pinSentSuccess = false;
          this.step2ErrorType = 'expired';
          this.pinExpired.emit();
        }
      } else {
        this.resendCooldownSeconds = remaining;
      }
    }, 1000);
  }

  private clearResendCooldown() {
    if (this.resendTimerId) {
      clearInterval(this.resendTimerId);
      this.resendTimerId = null;
    }
  }

  reset() {
    this.otpForm.reset();
    this.selectedPinDeliveryMethod = null;
    this.showSendPinButton = true;
    this.hasSentPinOnce = false;
    this.pinSentSuccess = false;
    this.clearResendCooldown();
    this.resendCooldownSeconds = 0;
    this.step2ErrorType = null;
    this.pinAlreadyValidated = false;
  }

  private sanitizePhoneNumber(value?: string) {
    return (value || '').replace(/-/g, '');
  }

  private retrieveWirelessResponse(resp: any) {
    return resp.ResponseHeader?.ProductResponse?.find(
      (response: any) => response.Type.toLowerCase() === 'wireless'
    );
  }

  ngOnDestroy(): void {
    this.clearResendCooldown();
    this.subscriptions.unsubscribe();
  }
}